import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { getAdvertisementStatistics } from '@/api/sponsorApi'
import { STATISTICS_DATA_FETCH_TIMEOUT } from '@/constants'
import { useMemo, useState } from 'react'
import { ClickIcon, ClickSquareIcon, TotalImpressionsIcon } from '../Icons'
import { formatGrowth } from '@/utils/helper'
import { DollarFilledIcon } from '../Icons/DollarFilledIcon'
import { useQuery } from '@tanstack/react-query'
import { SectionCards, type DateFilterValues } from '../section-cards'

interface IAdminAdvertisementStatItem {
  count: number
  percentageChange: number
}

export type IAdminAdvertisementStatistics = Record<
  'averageCTR' | 'revenue' | 'totalImpressions' | 'totalClicks',
  IAdminAdvertisementStatItem
>

export function AdStatistics({ showLabel }: Readonly<{ showLabel?: boolean }>) {
  const [dateFilters, setDateFilters] = useState<DateFilterValues>()

  const { data: statisticsData, isLoading: isLoadingStats } = useQuery({
    queryKey: [
      SPONSOR_QUERY_KEYS.advertisementStatistics('admin'),
      dateFilters,
    ],
    queryFn: () => {
      return getAdvertisementStatistics({
        source: 'admin',
        ...(dateFilters && {
          month: dateFilters.month,
          year: dateFilters.year,
        }),
      })
    },
    refetchInterval: STATISTICS_DATA_FETCH_TIMEOUT,
    refetchIntervalInBackground: true,
    enabled: dateFilters !== undefined,
  })

  const adCards = useMemo(() => {
    const stats: IAdminAdvertisementStatistics = statisticsData?.data

    return [
      {
        title: 'Total Impressions',
        value: stats?.totalImpressions?.count,
        icon: <TotalImpressionsIcon />,
        cardType: 1 as const,
        growth: formatGrowth(stats?.totalImpressions?.percentageChange),
      },
      {
        title: 'Total Clicks',
        value: stats?.totalClicks?.count,
        icon: <ClickSquareIcon />,
        cardType: 2 as const,
        growth: formatGrowth(stats?.totalClicks?.percentageChange),
      },
      {
        title: 'Average CTR',
        value: stats?.averageCTR?.count,
        icon: <ClickIcon />,
        cardType: 3 as const,
        growth: formatGrowth(stats?.averageCTR?.percentageChange),
      },
      {
        title: 'Ad Revenue',
        value: stats?.revenue?.count,
        icon: <DollarFilledIcon />,
        cardType: 4 as const,
        growth: formatGrowth(stats?.revenue?.percentageChange),
      },
    ]
  }, [statisticsData])

  return (
    <div className="flex flex-col gap-1.5">
      <SectionCards
        cards={adCards}
        isLoading={isLoadingStats}
        showFilters
        cardLabel={showLabel ? 'Advertisement Statistics' : undefined}
        filterValues={dateFilters}
        onFilterChange={val => setDateFilters(val)}
      />
    </div>
  )
}
